import Card from '@pelckmans/elementis/components/card';
import Pill from '@pelckmans/elementis/components/pill';
import { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import IdentityContext from '../../../contexts/IdentityContext';
import { useRoutes } from '../../../hooks/useRoutes';
import { UserModule } from '../api/methodsApi';
import { buildSubtitle, hasAccess } from '../utils';
import UserModuleStatus from './UserModuleStatus';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../constants/analyticsEvents';
import useAnalytics from '../../../hooks/useAnalytics';

function Method({
  module,
  position,
  visitedModules,
  years,
  isNew,
  scrollTo,
}: {
  module: UserModule;
  position: number;
  visitedModules: string[];
  years: number[];
  isNew?: boolean;
  scrollTo?: boolean;
}) {
  const { REACT_APP_P_API_URL, REACT_APP_P_STUDIO_GROUP } = process.env;
  const { catalogName, subjectName, image, id, publishingState, subjectSlug, slug, methodSlug, externalContentUrl } =
    module;

  const { buildAbsolutePath } = useRoutes();
  const { t } = useTranslation();
  const { identity } = useContext(IdentityContext);

  const userHasAccess = hasAccess(identity, publishingState, id);

  const moduleRef = useRef<HTMLDivElement>(null);

  const translations = {
    year: t('PAGES.MY_METHODS.MODULES.PILLS.YEAR'),
    primary: t('PAGES.MY_METHODS.MODULES.PILLS.SUFFIX.PRIMARY'),
    standard: t('PAGES.MY_METHODS.MODULES.PILLS.SUFFIX.STANDARD'),
  };

  const defaultCard = REACT_APP_P_STUDIO_GROUP === 'SO' || REACT_APP_P_STUDIO_GROUP === 'ES';

  // scale in animation (when new module added)
  useEffect(() => {
    if (moduleRef.current && isNew) {
      const module = moduleRef.current;
      module.classList.add('scale-in');
    }
  }, [isNew, module.id]);

  // scroll animation (when new module added)
  useEffect(() => {
    if (moduleRef.current && scrollTo) {
      const moduleEl = moduleRef.current;
      const mainEl = document.querySelector('main');
      if (mainEl) {
        const yOffset = 300;
        const y = moduleEl.getBoundingClientRect().top + mainEl.scrollTop - yOffset;
        mainEl.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
  }, [module.id, scrollTo]);
  const routeParams = { subjectSlug, methodSlug, moduleSlug: slug };

  const analytics = useAnalytics();

  const logEnodoEvent = async () => {
    await analytics.capture({
      eventType: ANALYTICS_EVENT_TYPES.MODULE_ACCESSED,
      objectId: module.id,
      subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_DASHBOARD,
    });
  };

  return (
    <div className="module" ref={moduleRef} id={module.id} data-testid="module">
      <div className="pill">{position === 0 && <Pill>{buildSubtitle(translations, years)}</Pill>}</div>
      <Card
        as="link"
        disabled={!userHasAccess}
        status={{
          children: (
            <UserModuleStatus access={userHasAccess} module={module} newModule={!visitedModules.includes(id)} />
          ),
          'aria-label': t('PAGES.MY_METHODS.MODULES.CARDS.STATUS_GROUP.A11Y_LABEL'),
          role: 'group',
        }}
        titleText={defaultCard ? subjectName : catalogName}
        subText={defaultCard ? catalogName : subjectName}
        titleTag="h3"
        image={`${REACT_APP_P_API_URL}${image}`}
        imagePosition="top"
        onClick={logEnodoEvent}
        {...(externalContentUrl
          ? {
              external: true,
              externalAriaLabel: t('PAGES.MY_METHODS.MODULES.CARDS.EXTERNAL.A11Y_LABEL', {
                method: defaultCard ? subjectName : catalogName,
              }),
              target: '_blank',
              rel: 'noopener noreferrer',
              href: externalContentUrl,
            }
          : {
              href: buildAbsolutePath(['MATERIAL'], routeParams),
            })}
      />
    </div>
  );
}

export default Method;
